/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ApiRequestPendingInterface,
  RequestPendingApiResponseInterface,
  RequestPendingInitialProperties,
} from '../../interface/request-pending-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import penaltyManagementApi from '../../utils/api/penalty-management';

export const fetchRequestPending = createAsyncThunk<
  RequestPendingApiResponseInterface, // Return type
  ApiRequestPendingInterface
>('request-pending/fetchRequestPending', async (data: ApiRequestPendingInterface, { dispatch }) => {
  try {
    const response = await penaltyManagementApi.fetchRequestPending(removeEmptyKeys({ ...data }));
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

export const fetchRequestPendingFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'request-pending/fetchPendingFilters',
  async (_, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: RequestPendingInitialProperties = {
  requestPending: [],
  limit: DefaultApiLimit.limit,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const requestPendingSlice = createSlice({
  name: 'request-pending',
  initialState,
  reducers: {
    /**
     * Update the pending filters
     * @param state
     * @param action
     */
    updatePendingFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disablePendingFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function to change sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Function to handle async actions
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<RequestPendingInitialProperties>) => {
    builder
      .addCase(fetchRequestPending.pending, (state: RequestPendingInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchRequestPending.fulfilled,
        (
          state: RequestPendingInitialProperties,
          action: PayloadAction<RequestPendingApiResponseInterface>,
        ) => {
          state.loading = false;
          state.requestPending = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchRequestPending.rejected, (state: RequestPendingInitialProperties) => {
        state.loading = false;
      });
    builder
      .addCase(fetchRequestPendingFilters.pending, (state: RequestPendingInitialProperties) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchRequestPendingFilters.fulfilled,
        (
          state: RequestPendingInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...(action.payload || [])];
        },
      )
      .addCase(fetchRequestPendingFilters.rejected, (state: RequestPendingInitialProperties) => {
        state.filterListLoading = false;
      });
  },
});
export const { updatePendingFilters, disablePendingFilterApplied, setSortData } =
  requestPendingSlice.actions;
export default requestPendingSlice.reducer;
